import React, { useEffect, useMemo, useState } from "react";
import { Divider, Form, Input, message, Modal, Select } from "antd";
import ModelSelector from "./components/ModelSelector";
import UIFetchService from "../../Service/UIFetchService";
import { PlaceHolderItem, Projection } from "./model";
import PlaceholderFilter from "./components/PlaceholderFilter";
import TimeDimension from "./components/TimeDimension";
import { indicatorJoin } from "./constant";
import { createIndicator, getIndicator, updateIndicator } from "./api";
import IndicatorFolderSelector from "./components/IndicatorFolderSelector";

const { useForm } = Form;

type Props = {
  visible: boolean;
  onClose: () => void;
  afterEdit: () => void;
  id?: number;
};

const IndexEditModal: React.FC<Props> = ({
  visible,
  id,
  onClose,
  afterEdit,
}) => {
  const isEdit = useMemo(() => {
    return id ? true : false;
  }, []);
  // 名称中是否含有 ‘cpoy’
  const [isCopy, setIsCopy] = useState(false);
  const [form] = useForm();

  const modelFormChange = (changedFields) => {
    for (const item of changedFields) {
      if (item.name[0] === "model") {
        // 触发渲染模板
        form.setFieldsValue({ dimensions: [] });
        renderModelTemplate(item.value);
        break;
      }
    }
  };
  const [modelPlaceholder, setModelPlaceholder] = useState<{
    value: string;
    text: string;
  }>();

  const [placeHolders, setPlaceHolders] = useState<PlaceHolderItem[]>([]);
  const [indicators, setIndicators] = useState<Projection[]>([]);
  const [dimensions, setDimensions] = useState<Projection[]>([]);
  const [md, setMd] = useState({});

  // 每次显示前重置表单
  useEffect(() => {
    visible && form.resetFields();
    // 填充表单
    if (id !== undefined) {
      setLoading(true);
      getIndicator(id)
        .then((res) => {
          const parsedPlaceHolder = {};
          Object.keys(res.placeHolder).forEach((key) => {
            parsedPlaceHolder[key] = res.placeHolder[key].split(",");
          });
          if (res.name && res.name.includes("copy")) {
            setIsCopy(true);
          } else {
            setIsCopy(false);
          }
          const fill = {
            name: res.name,
            directoryId: res.directoryId + "",
            desc: res.comment,
            model: res.templateId + "",
            placeHolder: parsedPlaceHolder,
            indicator: [
              res.metric.name,
              res.metric.expression,
              res.metric.kind,
            ].join(indicatorJoin),
            timeDimension: [
              res.timeDimension.name,
              res.timeDimension.expression,
              res.timeDimension.kind,
            ].join(indicatorJoin),
            timeFormat: res.timeDimension.timeGuarantee,
            timeOffset: res.timeDimension.offset,
          };
          renderModelTemplate(res.templateId);
          form.setFieldsValue(fill);
        })
        .catch((e) => {
          message.error("获取指标数据错误" + e.toString());
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      renderModelTemplate(null);
    }
  }, [visible]);

  // 查询筛选器列表并渲染
  const renderModelTemplate = async (id: number | string | null) => {
    if (!id) {
      setPlaceHolders([]);
      // 必选逻辑：看requires 如果没有就是必选，有的话看值
      // columm 如果为空必选
      setMd({
        ...md,
        staffs: [],
      });
      setIndicators([]);
      setDimensions([]);
      return;
    }
    try {
      const res = await UIFetchService.get(
        `/api/v1/querying_guide/templates/${id}`
      );
      setModelPlaceholder({
        text: res.name,
        value: res.id,
      });
      res.placeholders && setPlaceHolders(res.placeholders);
      // 必选逻辑：看requires 如果没有就是必选，有的话看值
      // columm 如果为空必选
      res.staffs &&
        setMd({
          ...md,
          staffs: res.staffs,
        });
      if (res.projection && Array.isArray(res.projection)) {
        setIndicators(res.projection.filter((pro) => pro.kind === "INDICATOR"));
        setDimensions(res.projection.filter((pro) => pro.kind === "DIMENSION"));
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    Promise.all([
      UIFetchService.get("/api/v1/metadata/provinces/tree"),
      UIFetchService.get("/api/v1/metadata/word_books/tree"),
      UIFetchService.get("/api/v1/metadata/ad/to_groups_to_objects"),
      UIFetchService.get("/api/v1/metadata/ad/ad_resource_slots"),
    ]).then((resArr) => {
      setMd({
        ...md,
        provinces: resArr[0],
        bookCategories: resArr[1],
        toGroupsToObjects: resArr[2],
        adSlots: resArr[3],
      });
    });
  }, []);

  const [loading, setLoading] = useState(false);

  const confirm = () => {
    setLoading(true);
    form
      .validateFields()
      .then((values) => {
        message.success("验证通过");
        const filteredPlaceholder = {};
        Object.keys(values.placeHolder)
          .filter(
            (key) =>
              values.placeHolder[key] !== undefined &&
              values.placeHolder[key] !== null
          )
          .forEach((key) => {
            filteredPlaceholder[key] = Array.isArray(values.placeHolder[key])
              ? values.placeHolder[key].join(",")
              : (values.placeHolder[key] as string);
          });
        const td = values.timeDimension.split(indicatorJoin);
        const metricArr = values.indicator.split(indicatorJoin);
        const reqData = {
          name: values.name,
          comment: values.desc,
          projectGroupName: values.group,
          bizProcess: values.process,
          templateId: values.model,
          placeHolder: filteredPlaceholder,
          timeDimension: {
            name: td[0],
            expression: td[1],
            kind: td[2],
            timeGuarantee: values.timeFormat,
            offset: values.timeOffset,
          },
          metric: {
            name: metricArr[0],
            expression: metricArr[1],
            kind: metricArr[2],
          },
        };
        return isEdit
          ? updateIndicator({ ...reqData }, id as number)
          : createIndicator(reqData);
      })
      .then(() => {
        afterEdit();
        message.success("保存成功！");
        onClose();
      })
      .catch((e) => {
        message.error("保存失败，请确认问题");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal
      title={`${isEdit ? "编辑" : "新建"}订阅指标`}
      visible={visible}
      onCancel={onClose}
      bodyStyle={{ maxHeight: "500px", overflow: "auto" }}
      onOk={confirm}
      maskClosable={false}
      confirmLoading={loading}
    >
      <Form form={form} labelCol={{ span: 6 }} onFieldsChange={modelFormChange}>
        <Form.Item
          name="name"
          label="订阅指标名称"
          rules={[{ required: true, message: "请输入指标名称" }]}
          labelCol={{ span: 6 }}
        >
          <Input placeholder="订阅指标名称" disabled={isEdit && !isCopy} />
        </Form.Item>
        <IndicatorFolderSelector
          name="directoryId"
          label="所属文件夹"
          rules={[{ required: true, message: "请选择文件夹" }]}
          labelCol={{ span: 6 }}
        />
        <Form.Item name="desc" label="订阅指标说明" labelCol={{ span: 6 }}>
          <Input placeholder="订阅指标说明" />
        </Form.Item>

        <Divider />
        <ModelSelector
          name="model"
          label="所属天机模板"
          rules={[{ required: true, message: "请选择模板" }]}
          placeholder={modelPlaceholder}
        />
        <PlaceholderFilter placeholders={placeHolders} md={md} />
        <Divider />
        {indicators && indicators.length > 0 && (
          <Form.Item name="indicator" label="指标" required={true}>
            <Select style={{ width: 200 }}>
              {indicators.map((indicator) => (
                <Select.Option
                  key={indicator.id}
                  value={[indicator.name, indicator.expr, indicator.kind].join(
                    indicatorJoin
                  )}
                  style={{ width: 200 }}
                >
                  {indicator.desc}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}
        {dimensions && dimensions.length > 0 && (
          <TimeDimension timeDimensions={dimensions} />
        )}
      </Form>
    </Modal>
  );
};

export default IndexEditModal;
